<template>
  <section class="section-base form-section-base">
    <div class="form-section-base__title">
      Сведения о доверенности в машиночитаемом виде
    </div>

    <v-container>
      <div class="form-base">
        <MrpaUploader
          :v="$v"
          @parsed-mrpa-change="parsedMrpaChangeHandler"
          :statement-id="$route.params.statementId"
          :statement-type="StatementType.EGRN_CONFIRM"
        />

        <template v-if="formVisible">
          <div class="form-section-base__controls">
            <v-row>
              <v-col>
                <v-radio-group v-model="role" row disabled>
                  <v-radio label="Физическое лицо" :value="Role.PERSON" />
                  <v-radio
                    label="Юридическое лицо"
                    :value="Role.ORGANIZATION"
                  />
                  <v-radio
                    label="Иностранное юридическое лицо"
                    :value="Role.FOREIGN_ORGANIZATION"
                  />
                </v-radio-group>
              </v-col>
            </v-row>
          </div>

          <MrpaPerson v-if="role === Role.PERSON" :v="$v.applicant.person" />

          <MrpaOrganization
            v-if="role === Role.ORGANIZATION"
            :v="$v.applicant.organization"
          />

          <MrpaForeignOrganization
            v-if="role === Role.FOREIGN_ORGANIZATION"
            :v="$v.applicant.foreignOrganization"
          />
        </template>

        <Footer
          @next-btn-click="btnSaveClickHandler(StepDirection.NEXT)"
          @prev-btn-click="btnSaveClickHandler(StepDirection.PREV)"
          :loading-next="loadingNext"
        />
      </div>
    </v-container>
  </section>
</template>

<script>
import { mdiAlertCircleOutline } from '@mdi/js';
import { Role } from '@/utils/dictionaries';
import { leaveStep, checkInvalid } from '@/utils/services';
import Footer from '@/components/services/elements/Footer';
import { StepDirection, StepName } from '@/types/services';
import { getValidationErrorText } from '@/utils/validate';
import cloneDeep from 'clone-deep';
import { ServicesModule } from '@/store/modules/services';
import deepmerge from 'deepmerge';
import { MrpaType, StatementType } from '@/constants';
import MrpaUploader, {
  getMrpa,
  mrpaUploaderModel,
  mrpaUploaderValidation,
  prefillMrpDisabled,
} from '@/components/services/elements/MrpaUploader';
import MrpaPerson, {
  mrpaPersonModel,
  getMrpaPersonValidation,
} from './MrpaPerson';
import MrpaOrganization, {
  mrpaOrganizationModel,
  getMrpaOrganizationValidation,
} from './MrpaOrganization';
import MrpaForeignOrganization, {
  mrpaForeignOrganizationModel,
  getMrpaForeignOrganizationValidation,
} from './MrpaForeignOrganization';
import { MrpaSubjectModule } from '@/store/modules/mrpaSubject';
import {
  assemblyMrpaTrustee,
  getDefaultMachineTrustee,
} from '@/components/services/elements/MrpaTrustee';

const getDefaultForm = () => ({
  person: cloneDeep(mrpaPersonModel),
  organization: cloneDeep(mrpaOrganizationModel),
  foreignOrganization: cloneDeep(mrpaForeignOrganizationModel),
});

export default {
  name: 'Mrpa',
  props: {
    loadingNext: {
      type: Boolean,
      required: true,
    },
    roleType: {
      type: String,
      required: true,
    },
  },
  components: {
    MrpaPerson,
    MrpaOrganization,
    MrpaForeignOrganization,
    Footer,
    MrpaUploader,
  },
  data() {
    return {
      StatementType,
      mdiAlertCircleOutline,
      getValidationErrorText,
      mrpa: cloneDeep(mrpaUploaderModel.mrpa),
      formVisible: mrpaUploaderModel.formVisible,
      applicant: getDefaultForm(),
      StepDirection,
      StepName,
      leaveStep,
      Role,
      invalidFields: [],
      role: Role.FOREIGN_ORGANIZATION,
      trusteeRole: Role.PERSON,
    };
  },
  validations() {
    return {
      ...mrpaUploaderValidation,
      applicant: {
        person: getMrpaPersonValidation(this.applicant.person),
        organization: getMrpaOrganizationValidation(
          this.applicant.organization
        ),
        foreignOrganization: getMrpaForeignOrganizationValidation(
          this.applicant.foreignOrganization
        ),
      },
    };
  },
  created() {
    const applicantStore = cloneDeep(
      ServicesModule.statement.subjects.applicant
    );

    if (applicantStore?.trustee) {
      this.prefillDraft(applicantStore, false);
    }
  },
  methods: {
    prefillDraft(applicant, isMrpa) {
      if (isMrpa) {
        prefillMrpDisabled(applicant);
      }

      if (applicant.person) {
        this.role = Role.PERSON;

        this.applicant.person = deepmerge.all([
          mrpaPersonModel,
          applicant.person,
          {
            trustee: getDefaultMachineTrustee(applicant.trustee),
          },
        ]);
      }

      if (applicant.organization) {
        this.role = Role.ORGANIZATION;
        this.applicant.organization = deepmerge.all([
          mrpaOrganizationModel,
          applicant.organization,
          {
            trustee: getDefaultMachineTrustee(applicant.trustee),
          },
        ]);
      }

      if (applicant.foreignOrganization) {
        this.role = Role.FOREIGN_ORGANIZATION;
        this.applicant.foreignOrganization = deepmerge.all([
          mrpaForeignOrganizationModel,
          applicant.foreignOrganization,
          {
            trustee: getDefaultMachineTrustee(applicant.trustee),
          },
        ]);
      }

      this.mrpa = getMrpa(applicant);
      this.formVisible = true;
    },

    assemblyModel() {
      const applicantEntity = cloneDeep(this.applicant);
      const trustee = applicantEntity[this.role].trustee;

      delete applicantEntity[this.role].trustee;

      return {
        [this.role]: applicantEntity[this.role],
        mrpType: this.mrpa.type,
        trustee: assemblyMrpaTrustee({
          trustee,
          mrpa: this.mrpa,
        }),
      };
    },

    async touchFields() {
      this.invalidFields = [];

      this.$v.formVisible.$touch();

      if (this.$v.formVisible.$invalid) {
        checkInvalid(this.$v.formVisible, this.invalidFields);
        return;
      }

      this.$v.applicant[this.role].$touch();

      checkInvalid(this.$v.applicant[this.role], this.invalidFields);

      if (this.invalidFields.length === 0) {
        if (this.mrpa.type === MrpaType.BY_GUID) {
          const isMrpaValid = await this.checkMrpaGuid();

          if (!isMrpaValid) this.invalidFields.push(true);
        }
      }
    },

    parsedMrpaChangeHandler(parsedMrpa) {
      if (parsedMrpa) {
        this.prefillDraft(parsedMrpa, true);
        this.formVisible = true;
      } else {
        this.role = Role.PERSON;
        this.applicant = getDefaultForm();
        this.formVisible = null;
      }
    },

    async checkMrpaGuid() {
      try {
        await MrpaSubjectModule.checkMrpaSubjectGuid({
          statementId: this.$route.params.statementId,
          guid: this.mrpa.guid,
          issuerInn: this.mrpa.issuerInn,
        });

        return true;
      } catch (e) {
        return false;
      }
    },

    btnSaveClickHandler(direction) {
      leaveStep({
        stepThis: this,
        direction,
        step: StepName.S2,
      });
    },
  },
};
</script>
